
import { useCallback, startTransition } from 'react'
import {
  type ReactFlowInstance,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type Connection,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
} from '@xyflow/react'
import type { CanvasNodeData } from '@/types/canvas'
import { isValidConnection } from '@/components/canvas/utils/connection-validator'
import { resolveHandlesForNode } from '@/lib/canvas/configs/handles'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { commitResizeChanges, shouldPersistNodeChanges } from '@/lib/canvas/node-box'

interface UseCanvasNodeHandlersParams {
  canvasRef: React.RefObject<ReactFlowInstance | null>
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  setSelectedNodes: React.Dispatch<React.SetStateAction<string[]>>
  saveNodes: (nodes: Node[]) => void
  saveEdges: (edges: Edge[]) => void
}

interface UseCanvasNodeHandlersReturn {
  handleNodeSelect: (nodeId: string, isShiftKey?: boolean) => void
  handleNodeRename: (nodeId: string, newTitle: string) => Promise<void>
  handleSelectionChange: (params: { nodes: Node[]; edges: Edge[] }) => void

  handlePaneClick: () => void
  handleNodesChange: (changes: NodeChange[]) => void
  handleEdgesChange: (changes: EdgeChange[]) => void
  handleConnect: (connection: Connection) => void
  handleNodeStateChange: (nodeId: string) => (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void
}

/**
 * Manages node and edge event handlers for the canvas.
 *
 * Responsibilities:
 * - Node selection (single + shift-multi)
 * - Node rename
 * - React Flow change handlers (nodes, edges, connections)
 * - Node state change (for settings persistence)
 * - Parent/child cleanup on edge deletion
 */
export function useCanvasNodeHandlers({
  canvasRef,
  setNodes,
  setEdges,
  setSelectedNodes,
  saveNodes,
  saveEdges,
}: UseCanvasNodeHandlersParams): UseCanvasNodeHandlersReturn {
  // Node selection handler
  const handleNodeSelect = useCallback((nodeId: string, isShiftKey: boolean = false) => {
    if (isShiftKey) {
      // Toggle selection with shift key
      setSelectedNodes(prev => {
        const newSelection = prev.includes(nodeId)
          ? prev.filter(id => id !== nodeId)
          : [...prev, nodeId]

        // Sync to canvas (defer to avoid setState during render)
        queueMicrotask(() => {
          canvasRef.current?.setNodes((nds) => nds.map(n => ({ ...n, selected: newSelection.includes(n.id) })))
        })

        return newSelection
      })
    } else {
      // Single selection
      setSelectedNodes([nodeId])

      // Sync to canvas (defer to avoid setState during render)
      queueMicrotask(() => {
        canvasRef.current?.setNodes((nds) => nds.map(n => ({ ...n, selected: n.id === nodeId })))
      })
    }
  }, [canvasRef, setSelectedNodes])

  // Node rename handler
  const handleNodeRename = useCallback(async (nodeId: string, newTitle: string) => {
    // Use updater function to get latest state (avoid race with debounced saves)
    setNodes(prevNodes => {
      const updatedNodes = prevNodes.map(node => {
        if (node.id !== nodeId) return node

        const nodeData = node.data as unknown as CanvasNodeData
        const nodeState = (node.data.state as Record<string, unknown> | undefined) ?? {}
        return {
          ...node,
          data: {
            ...(nodeData || {}),
            title: newTitle,
            state: { ...nodeState, title: newTitle, titleSource: 'custom' },
            ...(nodeData?.component ? {
              component: {
                ...nodeData.component,
                state: {
                  ...nodeData.component.state,
                  title: newTitle,
                  titleSource: 'custom',
                },
              },
            } : {}),
          },
        }
      })

      saveNodes(updatedNodes)
      return updatedNodes
    })
  }, [setNodes, saveNodes])

  // Handle selection change from React Flow
  const handleSelectionChange = useCallback(({ nodes: selectedRFNodes }: { nodes: Node[]; edges: Edge[] }) => {
    startTransition(() => {
      setSelectedNodes(selectedRFNodes.map(n => n.id))
    })
  }, [setSelectedNodes])

  // Handle pane click - deselect all nodes
  const handlePaneClick = useCallback(() => {
    setSelectedNodes([])
  }, [setSelectedNodes])

  // Handle nodes change with auto-save
  // React Flow sends NodeChange[] events (position, selection, dimensions, etc.)
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    const hasDimensionChanges = changes.some(c => c.type === 'dimensions')

    setNodes(prev => {
      const applied = applyNodeChanges(changes, prev)
      const { nodes: updated, committed } = commitResizeChanges(applied, changes)
      // Measurements are not edits. A finished corner drag is, and so is a move.
      if (updated !== prev && shouldPersistNodeChanges(changes, committed)) {
        saveNodes(updated)
      }
      return updated
    })

    // React Flow v12: edges can't compute paths until nodes have measured
    // dimensions. When dimension changes arrive, force edges to re-render
    // so they pick up the now-available handle positions.
    // Use requestAnimationFrame to batch multiple dimension changes during initial load.
    //
    // Handles are positioned as fractions of the node's height, so a resize
    // (or a media load that changes a node's size) moves them — but React Flow
    // caches handle bounds per node and won't re-measure on its own. Without
    // updateNodeInternals, edges keep attaching to where the handles used to be.
    if (hasDimensionChanges) {
      const resizedIds = changes
        .filter((c): c is NodeChange & { type: 'dimensions'; id: string } => c.type === 'dimensions')
        .map(c => c.id)
      requestAnimationFrame(() => {
        const updateNodeInternals = useCanvasBridgeStore.getState().updateNodeInternals
        if (updateNodeInternals) {
          for (const nodeId of resizedIds) updateNodeInternals(nodeId)
        }
        setEdges(prev => [...prev])
      })
    }
  }, [setNodes, setEdges, saveNodes])

  // Handle edges change with auto-save
  // React Flow sends EdgeChange[] events (add, remove, select, etc.)
  const handleEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges(prev => {
      const updated = applyEdgeChanges(changes, prev)

      // Check if any edges were deleted
      const removeChanges = changes.filter((c): c is EdgeChange & { type: 'remove' } => c.type === 'remove')

      if (removeChanges.length > 0) {
        const deletedEdges = prev.filter(oldEdge =>
          !updated.some(newEdge => newEdge.id === oldEdge.id)
        )

        // Clean up parent/child relationships for deleted edges
        if (deletedEdges.length > 0) {
          setNodes(currentNodes => {
            const updatedNodes = currentNodes.map(node => {
              const wasChild = deletedEdges.some(edge =>
                edge.target === node.id && edge.targetHandle === 'parent-input'
              )

              if (wasChild) {
                const updatedNode = { ...node }
                delete updatedNode.parentId

                const updatedData = updatedNode.data as unknown as CanvasNodeData

                if (updatedData?.component?.state) {
                  const newState = { ...updatedData.component.state }
                  delete newState.parentNodeId
                  updatedNode.data = {
                    ...updatedData,
                    component: {
                      ...updatedData.component,
                      state: newState,
                    },
                  }
                }

                return updatedNode
              }

              return node
            })

            saveNodes(updatedNodes)
            return updatedNodes
          })
        }
      }

      if (updated !== prev) {
        saveEdges(updated)
      }
      return updated
    })
  }, [setEdges, setNodes, saveEdges, saveNodes])

  // Handle new connections (user drags handle to handle)
  const handleConnect = useCallback((connection: Connection) => {
    // Validate: no self-connections
    if (connection.source === connection.target) return

    // Validate connection (data type compatibility, cycles) — skip maxConnections
    // since we handle replacement below
    const nodes = canvasRef.current?.getNodes() || []
    const currentEdges = canvasRef.current?.getEdges() || []
    if (!isValidConnection(connection, nodes, currentEdges, { maxInputConnections: Infinity })) return

    setEdges(prev => {
      // Validate: no duplicate edges (same source + target + handles)
      const isDuplicate = prev.some(
        edge =>
          edge.source === connection.source &&
          edge.target === connection.target &&
          edge.sourceHandle === connection.sourceHandle &&
          edge.targetHandle === connection.targetHandle
      )
      if (isDuplicate) return prev

      // Replace existing connection on the target handle if maxConnections would be exceeded
      // This disconnects the old edge and connects the new one
      let edges = prev
      if (connection.targetHandle) {
        const targetNode = nodes.find(n => n.id === connection.target)
        const handles = targetNode ? resolveHandlesForNode(targetNode) : []
        const handleMeta = handles.find(h => h.id === connection.targetHandle)
        const maxConnections = handleMeta?.maxConnections ?? Infinity

        const existingOnHandle = edges.filter(
          e => e.target === connection.target && e.targetHandle === connection.targetHandle
        )
        if (existingOnHandle.length >= maxConnections) {
          // Remove oldest connections to make room
          const toRemove = new Set(
            existingOnHandle.slice(0, existingOnHandle.length - maxConnections + 1).map(e => e.id)
          )
          edges = edges.filter(e => !toRemove.has(e.id))
        }
      }

      const updated = addEdge({ ...connection, type: 'bezier' }, edges)
      saveEdges(updated)
      return updated
    })
  }, [canvasRef, setEdges, saveEdges])

  // Callback to save settings to node state
  const handleNodeStateChange = useCallback((nodeId: string) => {
    return (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => {
      setNodes((prevNodes) => {
        const updatedNodes = prevNodes.map((node) => {
          if (node.id !== nodeId) return node

          const nData = node.data as unknown as CanvasNodeData
          const currentState: Record<string, unknown> = (nData?.state as Record<string, unknown>) || nData?.component?.state || {}
          const newState = updater(currentState)

          return {
            ...node,
            data: {
              ...nData,
              // data.state — React Flow rendering path (read by node components)
              state: newState,
              // data.component.state — save/persistence path
              component: {
                ...(nData?.component || { type: '', state: {} }),
                state: newState,
              },
            },
          }
        })

        // Save to localStorage/DB
        saveNodes(updatedNodes)
        return updatedNodes
      })
    }
  }, [setNodes, saveNodes])

  return {
    handleNodeSelect,
    handleNodeRename,
    handleSelectionChange,
    handlePaneClick,
    handleNodesChange,
    handleEdgesChange,
    handleConnect,
    handleNodeStateChange,
  }
}
